import React, { useEffect, useRef, useState } from 'react'
import { Menu } from 'primereact/menu'
import { MenuItem } from 'primereact/menuitem'
import { Toast } from 'primereact/toast'
import { FaChevronRight, FaExternalLinkAlt, FaRegTrashAlt, FaSignOutAlt } from 'react-icons/fa'
import { User } from '../../../../../../common/types/user'
import { updateProfile } from '_services/users.service'
import { formatHeightMeasurement } from 'util/helpers.converters'
import { InputSwitch } from 'primereact/inputswitch'
import { FormType, IncomingFormPayload, createFormPayload } from 'types/payloads.users'

import DynamicButtonListMultiple from '../../_forms/_components/DynamicButtonListMultiple'
import DynamicButtonListSingle from '../../_forms/_components/DynamicButtonListSingle'
import { EducationOptions } from 'enums/EducationLevels'
import HeightInput from '../../_forms/_components/heightField'
import EditCardTemplate from '../../_forms/add_edit/_components/editCardLayoutSingle'
import EditCardTemplateMultiple from '../../_forms/add_edit/_components/editCardLayoutMultiple'
import PhotoUpload from '../../_components/profile/components/PhotoUpload'
import VideoInterviewsManage from '../../_components/profile/components/video-interviews/manage'
import InterestInput from '../../_forms/_components/InterestInput'
import QuestionsInput from '../../_forms/_components/QuestionsInput'
import { useNavigate } from 'react-router-dom'
import LifestyleChoicesEdit from '../../_forms/add_edit/_components/lifeStyleChoicesEdit'
import { ProfileVerify } from '../../_forms/_components/ProfileVerify'
import { EditableArea } from '../../_forms/_components/EditableArea'
import DeleteAccount from '../../_forms/add_edit/_components/deleteAccount'
import { Dialog } from 'primereact/dialog'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Purchase } from 'types/purchases'
import { formatDateToLocalStandard, formatPrice } from 'util/formatters'
import useSignalR,{ mkMethod } from '../../../../_core/hooks/useSignalR'
import { HubReceive } from 'enums/HubMethods'
import EditableField from '../../_forms/_components/EditableField'
import useAuth from '../../../../_core/hooks/useAuth'
import { useEnumOptions } from '../../../../_core/hooks'
import Skeleton from '../../../../components/skeleton'
import { useCurrentUser } from '../../../../_core/contexts/currentUser'
import { EnumNormalizedStructure } from 'types/enums.options'
import useResize from '../../../../_core/hooks/useResize'
import {SUPPORT_EMAIL} from '@env'
import { PopUpContextType, PopupContext } from '../../../../_core/contexts/popToast'
import { getValidationErrors } from 'util/validation.messages'
import { isAxiosError } from 'axios'

type SidebarMenuItem = Omit<MenuItem,'template'> & {
  action?:string
  value: string | string[] | null
  classLabel?: string
  classValue?: string
  icon?: React.ReactNode
  editTemplate?: (_:User) => React.ReactNode
  onClick?: () => void
  href?: string
  template:(_:SidebarMenuItem) => React.ReactNode
}

type EditProfileProps = {
  action?:string
  onUpdate:() => void
}

const EditProfile = (props:EditProfileProps) => {
  const navigate = useNavigate()
  const { openPopup } = React.useContext<PopUpContextType>(PopupContext)
  const [auth] = useAuth()
  const [currentUser,reloadCurrentUser] = useCurrentUser()
  const [editing, setEditing] = useState<SidebarMenuItem>()
  const toast = useRef<Toast>(null)
  const { nationalityData, bodyTypeData, religionData, politicalBeliefData } = useEnumOptions()
  const [showPurchaseModal, setShowPurchaseModal] = useState<boolean>(false)
  const breakpoint = useResize()

  const _ = useSignalR([
    mkMethod([HubReceive.Token,(_:string) => reloadCurrentUser()]),
  ])

  const handleUserUpdate = async (payload: IncomingFormPayload) => {
    try {
      await updateProfile(payload)
      reloadCurrentUser()
      setEditing(undefined)
      props.onUpdate()
    }
    catch (e) {
      if (isAxiosError(e)) {
        openPopup({
          'severity': 'error',
          'title': 'Error',
          'text': `${Object.entries(getValidationErrors(e.response?.data)).map(([key, value]) => `${key} ${value}`).join(', ')}`,
        })
      }
    }
  }

  const reload = async () => {
    reloadCurrentUser()
    props.onUpdate()
  }

  const itemRenderer = (item:SidebarMenuItem) => (
    <a href={item.href} className="d-flex profile-menu-items profile-menu-link-override button-description"
      onClick={() => item.editTemplate ? setEditing(item) : (item.onClick? item.onClick() : null)}>
      <div className="col text-start">
        <span className={`text-nowrap ${item.classLabel}`}>{item.label}</span>
      </div>
      <div className="col text-end">
        <span className={`text-nowrap ${item.classValue}`}>{item.value}</span>
      </div>
      <div className="">
        <span>{item.icon || <FaChevronRight />}</span>
      </div>
    </a>
  )

  const items = (user:User) : { label:string,items:SidebarMenuItem[] }[] => [
    {
      label: 'Membership Status',
      items: [
        {
          label: 'Membership',
          classValue: user.activePurchases && user.activePurchases.length > 0 ? 'color-success' : 'color-primary',
          value: user.activePurchases && user.activePurchases.length > 0 ? user.activePurchases[0].Product.name : 'Sign up now',
          template: itemRenderer,
          icon: <></>,
          ...(user.activePurchases && user.activePurchases.length == 0 ? {
              onClick: () => {
                navigate('/upgrade')
              }}
          : {
            onClick: () => {
              setShowPurchaseModal(true)
            }})
        },
        {
          label: 'Photo Verification',
          classValue: user.userVerifyPhoto?.approved_by ? 'color-success' : 'color-primary',
          value: user.userVerifyPhoto?.approved_by ? 'Photo Verified' : user.userVerifyPhoto ? 'Pending Approval' : 'Get Verified',
          template: itemRenderer,
          ...(!user.userVerifyPhoto ? {editTemplate: () =>
            <ProfileVerify
              onClose={async () => {
                setEditing(undefined)
              }}
            />} : {}),
          icon: <></>,
        },
      ],
    },
    {
      label: 'Information',
      items: [
        {
          action:'photos',
          label: 'Photos',
          value: 'Add or Remove Pictures' ,
          template: itemRenderer,
          editTemplate: () =>
            <EditCardTemplate
              nosave
              label="Photos"
              initialValue={undefined}
              onClose={() => setEditing(undefined)}
            >
              <PhotoUpload
                onChange={reload}
              />
            </EditCardTemplate>
        },
        {
          'action':'video-interviews',
          'label':'Video Interviews',
          'value':'Add or Remove Videos',
          'template':itemRenderer,
          'editTemplate':() =>
            <EditCardTemplate
              nosave
              label="Video Interviews"
              initialValue={undefined}
              onClose={() => setEditing(undefined)}
            >
              <VideoInterviewsManage
                // onUpload={async () => {await fetchData()}}
                // onRemove={async (url:string) => await deletePhoto(url,fetchData,() => {})}
              />
            </EditCardTemplate>
        },
        {
          label: 'First Name',
          value: user.userMetadata ? user.firstName : 'Missing data',
          template: itemRenderer,
          icon: <></>,
        },
        {
          label: 'Last Name',
          value: user.userMetadata ? user.lastName : 'Missing data',
          template: itemRenderer,
          icon: <></>,
        },
        {
          label: 'Nationality',
          classValue: user.userMetadata && user.userMetadata.Nationalities && user.userMetadata.Nationalities.length ? '' : 'color-primary',
          value: user.userMetadata && user.userMetadata.Nationalities && user.userMetadata.Nationalities.length
            ? user.userMetadata.Nationalities.map((n) => n.EnumNationality.nationality).join(', ')
            : 'Nationality missing',
          template: itemRenderer,
          editTemplate: () =>
            <EditCardTemplateMultiple
              label="Nationality"
              initialValue={user.userMetadata?.Nationalities?.map((x) => ({ 'display':x.EnumNationality.nationality,'id':x.EnumNationality.isoCode })) || [] as EnumNormalizedStructure[]}
              onClose={() => setEditing(undefined)}
              onSave={async (x) => {
                await handleUserUpdate(
                  createFormPayload(
                    FormType.Nationality,
                    { isoCode: x.map(y => y.id) }
                  )
                )
              }}
            >
              <DynamicButtonListMultiple
                value={[]}
                onChange={() => {}}
                primaryOptions={nationalityData.filter(x => !x.advancedOption)}
                secondaryOptions={nationalityData.filter(x => !!x.advancedOption)}
                optionLabel={(x) => x.display}
                optionLabelKey={(x) => x.id}
                maxSelection={3}
              />
            </EditCardTemplateMultiple>
        },
        {
          label: 'Interests',
          value: 'Select all of your interests!',
          template: itemRenderer,
          editTemplate: (user:User) =>
            <EditCardTemplate
              nosave
              label="Interests"
              initialValue={user.userInterests.map(y => y.interestName) || []}
              onClose={async (xs) => {
                handleUserUpdate(
                  createFormPayload(
                    FormType.Interests,
                    { interests:xs }
                  )
                )
              }}
            >
              <InterestInput
                value={[]}
                onChange={() => {}}
              />
            </EditCardTemplate>
        ,
        },
        {
          label: 'Occupation',
          classValue:user.userMetadata?.occupationName ? '' : 'color-primary',
          value: user.userMetadata?.occupationName
            ? user.userMetadata?.occupationName.substring(0,25) + (user.userMetadata?.occupationName.length > 25 ? '...' : '')
            : 'Occupation not provided',
          template: itemRenderer,
          editTemplate: (user:User) =>
            <EditCardTemplate
              label="Occupation"
              initialValue={user.userMetadata?.occupationName}
              onClose={() => setEditing(undefined)}
              onSave={async (x) => {
                if(x){
                  await handleUserUpdate(
                    createFormPayload(
                      FormType.Occupation,
                      { OccupationName:x }
                    )
                  )
                }
              }}
            >
              <EditableField
                maxLength={50}
                id="occupation"
                label=""
                errors={undefined}
              />
            </EditCardTemplate>
        },
        {
          label: 'Highest Education',
          classValue:user.userMetadata?.highestEducationLevel ? '' : 'color-primary',
          value: user.userMetadata?.highestEducationLevel ?? 'No education provided',
          template: itemRenderer,
          editTemplate: (user) =>
            <EditCardTemplate
              label="Highest Education"
              initialValue={user.userMetadata?.highestEducationLevel ? { 'display':user.userMetadata?.highestEducationLevel,'id':user.userMetadata?.highestEducationLevel } : null}
              onClose={() => setEditing(undefined)}
              onSave={async (x) => {
                await handleUserUpdate(
                  createFormPayload(
                    FormType.EducationLevel,
                    { 'HighestLevel':x ? x.id : null }
                  )
                )
              }}
            >
              <DynamicButtonListSingle
                value={null}
                onChange={() => {}}
                primaryOptions={EducationOptions}
                optionLabel={(x) => x.display}
                optionLabelKey={(x) => x.id}
              />
            </EditCardTemplate>
        },
        {
          label: 'About Me',
          value: 'Your "About Me" section!',
          template: itemRenderer,
          editTemplate: (user) =>
            <EditCardTemplate
              label="About Me"
              initialValue={user.userMetadata?.aboutMe || ''}
              onClose={() => setEditing(undefined)}
              onSave={async (x) => {
                if(x){
                  await handleUserUpdate(
                    createFormPayload(
                      FormType.AboutMe,
                      { inputText:x }
                    )
                  )
                }
              }}
            >
              <EditableArea id='aboutMe' label="" errors={undefined}/>
            </EditCardTemplate>,
        },
        {
          label: 'Body Type',
          classValue: user.userMetadata && user.userMetadata.BodyType ? '' : 'color-primary',
          value: user.userMetadata && user.userMetadata?.BodyType ? user.userMetadata?.BodyType.display : 'No body type selected',
          template: itemRenderer,
          editTemplate: () =>
            <EditCardTemplate
              label="Body Type"
              initialValue={user.userMetadata?.BodyType ? { 'display':user.userMetadata?.BodyType.display,'id':user.userMetadata?.BodyType.name } : null}
              onClose={() => setEditing(undefined)}
              onSave={async (x) => {
                await handleUserUpdate(
                  createFormPayload(
                    FormType.BodyType,
                    { 'bodyType':x ? x.id : null }
                  )
                )
               }}
            >
              <DynamicButtonListSingle
                vertical
                value={null}
                onChange={() => {}}
                primaryOptions={bodyTypeData}
                optionLabel={(x) => x.display}
                optionLabelKey={(x) => x.id}
              />
            </EditCardTemplate>
        },
        {
          label: 'Lifestyle Choices',
          value: 'Answer some questions!',
          template: itemRenderer,
          editTemplate: (user) =>
            <LifestyleChoicesEdit
              label=""
              onClose={() => {setEditing(undefined)}}
              onSave={async (smokes,drinks,weed,kids) => {
                await handleUserUpdate(
                  createFormPayload(
                    FormType.Lifestyle,
                    {
                      SmokeTobacco: smokes || '',
                      DrinkAlcohol: drinks || '',
                      HasOrWantsKids: kids || '',
                      SmokeWeed: weed || '',
                    }
                  )
                )
              }}
              smokes={user.userMetadata?.smokesTobacco || ''}
              drinks={user.userMetadata?.drinksAlcohol || ''}
              weed={user.userMetadata?.smokesWeed || ''}
              kids={user.userMetadata?.hasOrWantsKids || ''}
            />
        },
        {
          label: 'Religious Belief',
          classValue: user.userMetadata && user.userMetadata.Religion ? '' : 'color-primary',
          value: user.userMetadata && user.userMetadata.Religion ? user.userMetadata.Religion.display : 'No religion selected',
          template: itemRenderer,
          editTemplate: (user) =>
            <EditCardTemplate
              label="Religious Belief"
              initialValue={user.userMetadata?.Religion ? { 'display':user.userMetadata?.Religion.display,'id':user.userMetadata?.Religion.name } : null}
              onClose={() => setEditing(undefined)}
              onSave={async (x) => {
                await handleUserUpdate(
                  createFormPayload(
                    FormType.ReligiousBeliefs,
                    { 'ReligiousBelief':x ? x.id : null }
                  )
                )
              }}
            >
              <DynamicButtonListSingle
                value={null}
                onChange={() =>{}}
                primaryOptions={religionData.filter(x => !x.advancedOption)}
                secondaryOptions={religionData.filter(x => x.advancedOption)}
                optionLabel={(x) => x.display}
                optionLabelKey={(x) => x.id}
              />
            </EditCardTemplate>
        },
        {
          label: 'Political Belief',
          classValue: user.userMetadata && user.userMetadata.PoliticalBelief ? '' : 'color-primary',
          value: user.userMetadata && user.userMetadata.PoliticalBelief ? user.userMetadata.PoliticalBelief.display : 'No parties chosen',
          template: itemRenderer,
          editTemplate: (user) =>
            <EditCardTemplate
              label="Political Belief"
              initialValue={user.userMetadata?.PoliticalBelief ? { 'display':user.userMetadata?.PoliticalBelief.display,'id':user.userMetadata?.PoliticalBelief.name } : null}
              onClose={() => setEditing(undefined)}
              onSave={async (x) => {
                await handleUserUpdate(
                  createFormPayload(
                    FormType.PoliticalBeliefs,
                    { 'PoliticalBelief':x ? x.id : null }

                  )
                )
              }}
            >

            <DynamicButtonListSingle
              value={null}
              onChange={() => {}}
              primaryOptions={politicalBeliefData}
              optionLabel={(x) => x.display}
              optionLabelKey={(x) => x.id}
            />
          </EditCardTemplate>
        },
        {
          label: 'Height',
          classValue: user.userMetadata && user.userMetadata.heightMeasurement ? '' : 'color-primary',
          value: user.userMetadata && user.userMetadata?.heightMeasurement
            ? user.userMetadata?.heightMeasurement
              ? formatHeightMeasurement(user.userMetadata?.heightMeasurement).dualLocaleDisplay
              : null
            : 'No height entered',
          template: itemRenderer,
          editTemplate: (user) =>
            <EditCardTemplate
              label="How Tall Are You?"
              initialValue={user.userMetadata?.heightMeasurement || 0}
              onClose={() => setEditing(undefined)}
              onSave={async (x:number | null) => {
                await handleUserUpdate(
                  createFormPayload(
                    FormType.HeightMeasurement,
                    { measurement:x || null }
                  )
                )
              }}
            >
              <HeightInput
                value={0}
                onChange={() => { }}
              />
            </EditCardTemplate>

        },
        {
          label: 'Q & A',
          value: 'Q & A!',
          template: itemRenderer,
          editTemplate: (user) =>
            <EditCardTemplate
              nosave
              label="Q & A!"
              initialValue={user.userQuestions ?? []}
              onClose={async (xs) => {
                Promise.all(xs.map((x) => updateProfile({
                  'FormType':FormType.UserQuestion,
                  'Data':{
                    'questionId':x.questionId,
                    'response':x.questionOptionId,
                  },
                })))
              }}
            >
              <QuestionsInput value={[]} onChange={() => {}}/>
            </EditCardTemplate>
        },
      ],
    },
    {
      label: 'Control Visibility',
      items: [
        {
          label: 'Incognito State',
          value: null,
          classValue: user.isIncognito ? '' : 'color-primary',
          icon: <InputSwitch
            checked={user.isIncognito || false}
            onClick={async () => {
              await handleUserUpdate(
                createFormPayload(
                  FormType.IncognitoState,
                  { isIncognito: !user.isIncognito }
                )
              )
            }}

          />,
          template: itemRenderer,
        },
      ],
    },
    {
      label: 'Account Settings',
      items: [
        {
          label: 'Phone',
          value: user.phone ? user.phone : "Uh oh,it's missing",
          classValue: user.phone ? '' : 'color-primary',
          template: itemRenderer,
          icon: <></>,
        },
        {
          label: 'Email',
          value: user.email ? user.email : "Uh oh,it's missing",
          classValue: user.email ? '' : 'color-primary',
          template: itemRenderer,
          icon: <></>,
        },
        // {
        //   label: 'Push Notifications',
        //   value: 'Control your notifications!',
        //   template: itemRenderer,
        // },
        // {
        //   label: 'Location',
        //   value: user.countryFriendlyName ? user.countryFriendlyName : "Uh oh,it's missing",
        //   classValue: user.countryFriendlyName ? '' : 'color-primary',
        //   template: itemRenderer,
        //   icon: <></>,
        // },
      ],
    },
    // {
    //   label: 'Visuals',
    //   items: [
    //     {
    //       label: 'Appearance Settings',
    //       value: 'Light/Dark Theme',
    //       template: itemRenderer,
    //     },
    //   ],
    // },
    {
      label: 'Safety',
      items: [
        {
          label: 'Community Guidelines',
          value: null,
          icon: <FaExternalLinkAlt />,
          href: '/community-guidelines',
          template: itemRenderer,
        },
        // {
        //   label: 'Safety & Policy',
        //   icon: <FaExternalLinkAlt />,
        //   template: itemRenderer,
        // },
        {
          label: 'Safety Tips',
          value: null,
          icon: <FaExternalLinkAlt />,
          template: itemRenderer,
          href: '/tips-tricks',
        },
        // {
        //   label: 'Help & Support',
        //   icon: <FaExternalLinkAlt />,
        //   href: '/community-guidelines',
        //   template: itemRenderer,
        // },
      ],
    },
    {
      label: 'Legal',
      items: [
        {
          label: 'Privacy Policy',
          value: null,
          icon: <FaExternalLinkAlt />,
          template: itemRenderer,
          href: '/privacy-policy',
        },
        {
          label: 'Terms of Service',
          value: null,
          icon: <FaExternalLinkAlt />,
          href: '/terms-and-conditions',
          template: itemRenderer,
        },
      ],
    },
    {
      label: 'Account Management',
      items: [
        {
          label: 'Delete Account',
          value: null,
          icon: <FaRegTrashAlt size={20} />,
          template: itemRenderer,
          editTemplate: () =>
            <EditCardTemplate
              nosave
              back="Nevermind"
              label="Delete my account"
              initialValue={undefined}
              onClose={() => setEditing(undefined)}
            >
              <DeleteAccount />
            </EditCardTemplate>
        },
        {
          label: 'Log Out',
          value: null,
          icon: <FaSignOutAlt size={20} />,
          template: itemRenderer,
          onClick: () => {
            auth.logout(()=>{
              navigate('/')
            })
          }
        },
      ],
    },
  ]

  useEffect(() => {(async () => {
    setEditing(
      props.action
      ? items(await currentUser)
        .reduce((agg,x) => agg.concat(x.items),[] as SidebarMenuItem[])
        .find((x) => x.action === props.action)
      : undefined
    )
  })()},[props.action])

  return (
    <div className="d-flex flex-column h-100" style={{ 'minWidth':'350px' }}>
      <Toast ref={toast} />
      <Skeleton dependency={currentUser} structure={Array(12).fill({ 'className':'mb-1 py-3' })}>{(user) => <>
      {
          user.purchases && user.purchases.length > 0
            ?
            <Dialog header="Subscription Management" className={breakpoint.isMobile ? "w-100" : "w-50"} visible={showPurchaseModal}
              onHide={() => { if (!showPurchaseModal) return; setShowPurchaseModal(false); }}>
              {
                user.activePurchases.length > 0 && <div className="flex justify-content-between m-2">
                  <h5>Current Subscription: {user.activePurchases[0].Product.name}</h5>
                  <p>To cancel a subscription please email <a href={`mailto:${SUPPORT_EMAIL}`}>support@datingacrossthepond.com</a></p>
                  {/* <Button
                className="btn btn-danger"
                onClick={async () => {
                    reload()
                    setShowPurchaseModal(false)
                  })
                }}
              >Cancel Subscription</Button> */}
                </div>
              }
              {/*
              <DataTable value={user.purchases} tableStyle={{ minWidth: '19rem' }}
              >
                <Column field="Product.name" header="Product"></Column>
                <Column field="Price" header="Price" body={(x: Purchase) => <>{x.price ? formatPrice(x.price, x.currency) : 'N/A'}</>}></Column>
                <Column field="transactionDate" header="Date" body={(x: Purchase) => <>{formatDateToLocalStandard(new Date(x.transactionDate))}</>}></Column>
                         </DataTable> */}
                         
              {/* <Column field="transactionDate" header="Date" body={(x:Purchase,o) => <>{new Date(x.transactionDate)}</>}></Column> */}
    
            </Dialog>
        : undefined
      }
      {
        editing && editing.editTemplate
        ? <div className="h-100 vertical-scroll border border-1">{editing.editTemplate(user)}</div>
        : <Menu model={items(user)} className="w-100 p-menu-override vertical-scroll" />
      }
      </>}</Skeleton>
    </div>
  )
}

export default React.memo(EditProfile)
